import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { colors, grey, sidebar as sidebarDimensions, fontFamily } from '../../styles/tokens';

const SIDEBAR_WIDTH = sidebarDimensions.width;
const SIDEBAR_COLLAPSED_WIDTH = sidebarDimensions.collapsedWidth;

/**
 * Sidebar - Design System Yoco
 *
 * Styles:
 * - Background: blanc (#FFFFFF)
 * - Active: fond bleu tres clair, icone noir
 * - Hover: fond gris tres clair
 * - Icones: 18px, gris par defaut, noir si actif
 * - Texte: 13px medium, gris par defaut, noir si actif
 * - Deconnexion fixe en bas
 */
const Sidebar = ({ navItems = [], logo, title, onLogout, user }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(isTablet);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const handleNavClick = (item) => {
    if (item.action === 'logout' && onLogout) {
      onLogout();
    } else if (item.path) {
      navigate(item.path);
    }
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Logique active: match exact pour racines, prefix pour sous-routes
  const isActive = (path) => {
    if (!path) return false;
    if (path === '/admin' || path === '/coach') {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Separer les items normaux de l'item logout
  const mainNavItems = navItems.map(section => ({
    ...section,
    items: section.items.filter(item => item.action !== 'logout'),
  })).filter(section => section.items.length > 0);

  const logoutItem = navItems
    .flatMap(section => section.items)
    .find(item => item.action === 'logout');

  // Composant pour un item de navigation
  const NavItem = ({ item, active }) => {
    const Icon = item.icon;

    const navItemContent = (
      <ListItem disablePadding sx={{ px: 1 }}>
        <ListItemButton
          onClick={() => handleNavClick(item)}
          sx={{
            borderRadius: '6px',
            mb: 0.25,
            minHeight: 36,
            py: 0.75,
            px: 1.5,
            justifyContent: collapsed ? 'center' : 'flex-start',
            bgcolor: active ? colors.sidebarActive : 'transparent',
            '&:hover': {
              bgcolor: active ? colors.sidebarActive : colors.sidebarHover,
            },
          }}
        >
          {Icon && (
            <ListItemIcon
              sx={{
                color: active ? grey[800] : grey[400],
                minWidth: collapsed ? 0 : 32,
                justifyContent: 'center',
              }}
            >
              <Icon sx={{ fontSize: 18 }} />
            </ListItemIcon>
          )}
          {!collapsed && (
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '0.8125rem',
                fontWeight: active ? 600 : 500,
                color: active ? colors.textPrimary : grey[600],
              }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );

    if (collapsed) {
      return (
        <Tooltip title={item.label} placement="right">
          {navItemContent}
        </Tooltip>
      );
    }

    return navItemContent;
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: colors.sidebarBg,
      }}
    >
      {/* Header - Avatar + Prenom */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          px: 2,
          height: sidebarDimensions.headerHeight,
          minHeight: sidebarDimensions.headerHeight,
        }}
      >
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: grey[200],
                color: grey[600],
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {user?.firstName?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
            <Typography
              sx={{
                fontFamily: fontFamily.heading,
                fontSize: '0.9375rem',
                fontWeight: 600,
                color: colors.textPrimary,
              }}
            >
              {user?.firstName || title || 'Yoco'}
            </Typography>
          </Box>
        )}

        {collapsed && (
          <Tooltip title="Ouvrir le menu" placement="right">
            <IconButton
              onClick={handleDrawerToggle}
              size="small"
              sx={{
                color: grey[400],
                '&:hover': {
                  bgcolor: colors.sidebarHover,
                  color: grey[600],
                },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}

        {!isMobile && !collapsed && (
          <IconButton
            onClick={handleDrawerToggle}
            size="small"
            sx={{
              color: grey[400],
              '&:hover': {
                bgcolor: colors.sidebarHover,
                color: grey[600],
              },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      <Divider sx={{ borderColor: colors.sidebarBorder }} />

      {/* Navigation principale */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {mainNavItems.map((section, sectionIndex) => (
          <Box key={sectionIndex} sx={{ mb: 1.5 }}>
            {/* Section label */}
            {!collapsed && section.section && (
              <Typography
                sx={{
                  px: 2,
                  py: 0.75,
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: grey[400],
                }}
              >
                {section.section}
              </Typography>
            )}

            {/* Nav items */}
            <List disablePadding>
              {section.items.map((item, itemIndex) => (
                <NavItem
                  key={itemIndex}
                  item={item}
                  active={isActive(item.path)}
                />
              ))}
            </List>
          </Box>
        ))}
      </Box>

      {/* Logo + Logout fixe en bas sur la meme ligne */}
      <Box sx={{ mt: 'auto', pb: 2 }}>
        <Divider sx={{ borderColor: colors.sidebarBorder, mb: 1 }} />

        {/* Logo gauche + Logout droite */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            px: collapsed ? 1 : 2,
            py: 0.5,
          }}
        >
          {/* Logo */}
          {!collapsed && (
            <Box
              component="img"
              src="/static/logo_black.svg"
              alt="Yoco"
              sx={{
                height: 18,
                width: 'auto',
                opacity: 0.4,
              }}
            />
          )}

          {/* Logout icon */}
          {logoutItem && (
            <Tooltip title={logoutItem.label} placement="right">
              <IconButton
                onClick={() => handleNavClick(logoutItem)}
                size="small"
                sx={{
                  color: grey[400],
                  '&:hover': {
                    bgcolor: colors.sidebarHover,
                    color: grey[600],
                  },
                }}
              >
                <logoutItem.icon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );

  const drawerWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  return (
    <>
      {/* Mobile hamburger */}
      {isMobile && (
        <IconButton
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: theme.zIndex.drawer + 2,
            bgcolor: colors.sidebarBg,
            color: colors.textPrimary,
            border: `1px solid ${colors.sidebarBorder}`,
            '&:hover': { bgcolor: colors.sidebarHover },
          }}
        >
          <MenuIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}

      {/* Mobile drawer (overlay) */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH,
              boxSizing: 'border-box',
              bgcolor: colors.sidebarBg,
              borderRight: `1px solid ${colors.sidebarBorder}`,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Desktop drawer (permanent) */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              bgcolor: colors.sidebarBg,
              borderRight: `1px solid ${colors.sidebarBorder}`,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};

export { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH };
export default Sidebar;
